/**
 * CommentThread — Session notes chat container (Sendbird-backed in Phase 2).
 *
 * Phase 1: Static prop-driven render. No Sendbird SDK.
 * Phase 2: Wire up Sendbird subscription here.
 *
 * Hidden entirely when canChat = false (no blocked state shown — chat is opt-in).
 */

import React from 'react';
import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { TFunction } from '@dramadetector/shared';
import { GlassCard } from '../../glass/GlassCard';
import { ChatBubble } from './ChatBubble';
import { ChatInput } from './ChatInput';
import type { ChatMessage } from '../../../hooks/usePractitionerClientView';

interface CommentThreadProps {
  clientName: string;
  messages: ChatMessage[];
  canChat: boolean;
  onSend: (text: string) => void;
  t: TFunction<'practitioner'>;
}

export const CommentThread: React.FC<CommentThreadProps> = ({
  clientName,
  messages,
  canChat,
  onSend,
  t,
}) => {
  if (!canChat) return null;

  return (
    <View
      accessible={false}
      accessibilityLabel={t('chat.thread_label', { client: clientName } as any)}
    >
    <GlassCard
      withChromeBorder={false}
      noPadding={true}
    >
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{t('chat.session_notes')}</Text>
        <View style={styles.sendbirdLabel}>
          <Ionicons name="chatbubble-outline" size={12} color="#6B7280" />
          <Text style={styles.sendbirdText}>{t('chat.via_sendbird')}</Text>
        </View>
      </View>

      {/* Message list */}
      <View style={styles.messageList}>
        {messages.map((msg) => (
          <ChatBubble key={msg.id} message={msg} />
        ))}
      </View>

      {/* Input */}
      <ChatInput onSend={onSend} t={t} />
    </GlassCard>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.4)',
  } as ViewStyle,
  headerTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#374151',
  },
  sendbirdLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  } as ViewStyle,
  sendbirdText: {
    fontSize: 12,
    color: '#6B7280',
  },
  messageList: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 12,
  },
});
